import React, { useEffect, useMemo, useState } from "react";
import { authHeaders } from "../../utils/auth";
import { jwtDecode } from "jwt-decode";
import { Link } from "react-router-dom";
import FiltersBar, {
  handQueryValue,
  hasRequiredFilters as filtersReady,
} from "../../components/DashBoard/FiltersBar";
import AccuracyGraph from "../../components/AccuracyGraph";
import ResultsSection from "../../components/DashBoard/ResultsSection";
import OrdersHistory from "../../components/DashBoard/OrderHistory";
import styles from "./patientDashboard.module.css";
import {
  PREVIEW_PATIENT_A,
  filterPreviewEntries,
  toGraphEntries,
} from "../../data/dashboardPreviewSample";

const baseURL = process.env.REACT_APP_BACKEND_API_URL;
const PAGE_SIZE = 10;

type Props = { previewMode?: boolean };

export default function PatientDashboard({ previewMode = false }: Props) {
  const [entries, setEntries] = useState<any[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [filterHand, setFilterHand] = useState(previewMode ? "Right" : "");
  const [filterExercise, setFilterExercise] = useState(
    previewMode ? "Vertical Mug" : ""
  );
  const [filterStartDate, setStartDate] = useState("");
  const [filterEndDate, setEndDate] = useState("");
  const [userEmail, setUserEmail] = useState(previewMode ? PREVIEW_PATIENT_A : "");
  const [name, setName] = useState(previewMode ? "Jordan" : "");
  const [datakey, setDataKey] = useState("Accuracy");
  const [lastMonths, setLastMonths] = useState<number>(6);
  const [page, setPage] = useState<number>(1);
  const hasRequiredFilters = filtersReady(filterHand, filterExercise);

  const handleSignOut = () => {
    localStorage.removeItem("idToken");
    localStorage.removeItem("accessToken");
    window.location.href = "/login";
  };

  const toISODate = (d: Date) => {
    const yyyy = d.getUTCFullYear();
    const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
    const dd = String(d.getUTCDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  useEffect(() => {
    const end = new Date();
    const endUTC = new Date(
      Date.UTC(end.getUTCFullYear(), end.getUTCMonth(), end.getUTCDate())
    );
    const startUTC = new Date(endUTC);
    startUTC.setUTCMonth(startUTC.getUTCMonth() - lastMonths);

    setStartDate(toISODate(startUTC));
    setEndDate(toISODate(endUTC));
  }, [lastMonths]);

  useEffect(() => {
    if (previewMode) return;

    const idToken = localStorage.getItem("idToken");
    if (!idToken) {
      return handleSignOut();
    }

    try {
      const decoded: any = jwtDecode(idToken);
      const groups = decoded["cognito:groups"] || [];

      if (!groups.includes("Patient") && !groups.includes("patient")) {
        return handleSignOut();
      }

      setUserEmail(decoded.email);
      setName(decoded.given_name);
    } catch (err) {
      console.error("❌ Error decoding token", err);
      handleSignOut();
    }
  }, [previewMode]);

  useEffect(() => {
    if (!userEmail) return;
    if (!hasRequiredFilters) {
      setEntries([]);
      setError(null);
      return;
    }

    if (previewMode) {
      const rows = filterPreviewEntries(userEmail, filterHand, filterExercise);
      setEntries(toGraphEntries(rows));
      setError(null);
      return;
    }

    const fetchEntries = async () => {
      try {
        setError(null);
        const params = new URLSearchParams();
        params.append("username", userEmail);
        params.append("hand", handQueryValue(filterHand, filterExercise));
        params.append("exerciseName", filterExercise);
        if (filterStartDate) params.append("start", filterStartDate);
        if (filterEndDate) params.append("end", filterEndDate);

        const res = await fetch(`${baseURL}api/aws/filtered?${params.toString()}`, { headers: authHeaders() });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || "Unknown error");
        setEntries(data.entries);
      } catch (err: any) {
        console.error("❌ Error fetching entries:", err);
        setError(err.message || "Failed to fetch entries.");
      }
    };

    fetchEntries();
  }, [
    userEmail,
    filterHand,
    filterExercise,
    filterStartDate,
    filterEndDate,
    hasRequiredFilters,
    previewMode,
  ]);

  useEffect(() => {
    setPage(1);
  }, [filterHand, filterExercise, filterStartDate, filterEndDate]);

  const sortedEntriesDesc = useMemo(
    () =>
      [...entries].sort(
        (a, b) => new Date(b.Timestamp).getTime() - new Date(a.Timestamp).getTime()
      ),
    [entries]
  );
  const pageCount = Math.max(1, Math.ceil(sortedEntriesDesc.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount);
  const currentSlice = sortedEntriesDesc.slice(
    (safePage - 1) * PAGE_SIZE,
    safePage * PAGE_SIZE
  );

  return (
    <div className={styles.page}>
      {previewMode ? (
        <p className="ui-banner ui-banner--brand" role="status">
          Preview with sample data only.{" "}
          <Link to="/login" className="ui-link">
            Sign in
          </Link>{" "}
          to use your account.
        </p>
      ) : null}
      <div className={styles.header}>
        <div className={styles.headerText}>
          <h1 className={styles.title}>Welcome {name}</h1>
          <p className={styles.subtitle}>
            {previewMode
              ? "Example dashboard for upper-limb practice after stroke, with the same layout as the real member view."
              : "Your latest sessions, summarized."}
          </p>
        </div>

        <div className={styles.rangeControl}>
          <label className={styles.rangeLabel} htmlFor="range-months">
            Showing the last
          </label>
          <select
            id="range-months"
            className={`ui-input ${styles.rangeSelect}`}
            value={String(lastMonths)}
            onChange={(e) => setLastMonths(Number(e.target.value))}
          >
            <option value="1">1 month</option>
            <option value="3">3 months</option>
            <option value="6">6 months</option>
            <option value="12">12 months</option>
          </select>
        </div>
      </div>

      <div className={`ui-card ui-card--lg ${styles.panel}`}>
        {!previewMode ? <OrdersHistory /> : null}

        <FiltersBar
          filterHand={filterHand}
          filterExercise={filterExercise}
          filterStartDate={filterStartDate}
          filterEndDate={filterEndDate}
          dataKey={datakey}
          setFilterHand={setFilterHand}
          setFilterExercise={setFilterExercise}
          setStartDate={setStartDate}
          setEndDate={setEndDate}
          setDataKey={setDataKey}
          showDateRange={false}
        />

        {error && (
          <p className="ui-banner ui-banner--error" role="alert">
            {error}
          </p>
        )}

        {!hasRequiredFilters ? (
          <p className={styles.note}>
            Select a hand and an exercise to view your results.
          </p>
        ) : null}

        {entries.length > 0 && <AccuracyGraph data={sortedEntriesDesc} dataKey={datakey} />}

        {currentSlice.length > 0 ? (
          <>
            <ResultsSection entries={currentSlice} selectedPatient={userEmail} />
            {previewMode ? (
              <div className={styles.ctaRow}>
                <Link to="/login" className="ui-btn">
                  Sign in for full results
                </Link>
              </div>
            ) : (
              <div className={styles.pagination}>
                <p className={styles.count}>
                  Showing {(safePage - 1) * PAGE_SIZE + 1} to{" "}
                  {Math.min(safePage * PAGE_SIZE, sortedEntriesDesc.length)} of{" "}
                  {sortedEntriesDesc.length}
                </p>
                <div className={styles.pageBtns}>
                  <button
                    type="button"
                    className="ui-btn ui-btn--secondary ui-btn--sm"
                    disabled={safePage <= 1}
                    onClick={() => setPage(Math.max(1, safePage - 1))}
                  >
                    Previous
                  </button>
                  <button
                    type="button"
                    className="ui-btn ui-btn--secondary ui-btn--sm"
                    disabled={safePage >= pageCount}
                    onClick={() => setPage(Math.min(pageCount, safePage + 1))}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        ) : hasRequiredFilters ? (
          <p className={styles.note}>No entries found for this selection.</p>
        ) : null}
      </div>

      {!previewMode ? (
        <div className={styles.footerRow}>
          <Link to="/profile/settings" className="ui-btn ui-btn--ghost">
            Settings
          </Link>
        </div>
      ) : null}
    </div>
  );
}
